import { differenceInCalendarDays, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatUsdAmount } from '#root/components/ui';
import { parseYmd } from '#root/utils';
import type { MatchReservationDTO } from '#root/models/PrepaidMatchDTO';

/** "31 oct" */
export const dayMonth = (date: Date): string => format(date, 'd MMM', { locale: es });

/** "4/nov" */
export const dayMonthSlash = (date: Date): string => format(date, 'd/MMM', { locale: es });

/**
 * Monto en dólares como texto plano (mensaje de WhatsApp, `aria`): "$1,390", "$1,199.50". En
 * pantalla se usa `Usd` del DS, con el mismo formato y el `$` más chico.
 */
export const usd = (value: number): string => `$${formatUsdAmount(value)}`;

/**
 * Frase de tax del detalle de precio. El FE solo repite lo que manda el BE: no calcula ni
 * supone nada. `> 0` → "con 7% de tax incluido"; `null` → "con el tax incluido, si aplica";
 * `0` → `null` (no se menciona tax).
 */
export const taxPhrase = (taxPercent: number | null | undefined): string | null => {
  if (taxPercent == null) return 'con el tax incluido, si aplica';
  if (taxPercent > 0) {
    // Hasta 3 decimales y sin ceros sobrantes: 7, 6.25, 8.875
    return `con ${taxPercent.toLocaleString('en-US', { maximumFractionDigits: 3 })}% de tax incluido`;
  }
  return null;
};

/** Un `Y-m-d H:i:s` del servidor (hora de Lima, sin zona) como `Date` con la misma hora */
const parseServerDate = (value: string): Date => new Date(value.replace(' ', 'T'));

/** Días (o menos) para que la fecha de entrega se marque como urgente */
const URGENT_DAYS = 3;

export interface DeliverByInfo {
  label: string;
  urgent: boolean;
}

/** Fecha límite y días que faltan contra el `server_time` (Lima); `null` si no hay fecha */
const daysUntil = (
  deliverBy: string | null,
  serverTime: string,
): { date: Date; daysLeft: number } | null => {
  const date = parseYmd(deliverBy);
  if (!date) return null;
  return { date, daysLeft: differenceInCalendarDays(date, parseServerDate(serverTime)) };
};

/**
 * "Entrega hasta el 31 oct", o "Entrega lo antes posible" si la fecha ya pasó. "Hoy" es el
 * `server_time` del feed (Lima), no el reloj del navegador. `urgent` cuando ya pasó o faltan
 * 3 días o menos; `null` si no hay fecha límite.
 */
export const deliverByInfo = (
  deliverBy: string | null,
  serverTime: string,
): DeliverByInfo | null => {
  const until = daysUntil(deliverBy, serverTime);
  if (!until) return null;
  if (until.daysLeft < 0) return { label: 'Entrega lo antes posible', urgent: true };
  return { label: `Entrega hasta el ${dayMonth(until.date)}`, urgent: until.daysLeft <= URGENT_DAYS };
};

/**
 * Único badge de un grupo con booster (reemplaza al de fecha): "Comisión mejorada ⚡ Entrega
 * antes del 31 oct"; vencida, "… ⚡ Entrega lo antes posible". Sin fecha, solo la mejora.
 */
export const boostLabel = (deliverBy: string | null, serverTime: string): string => {
  const until = daysUntil(deliverBy, serverTime);
  if (!until) return 'Comisión mejorada ⚡';
  if (until.daysLeft < 0) return 'Comisión mejorada ⚡ Entrega lo antes posible';
  return `Comisión mejorada ⚡ Entrega antes del ${dayMonth(until.date)}`;
};

/**
 * Hora de un `Y-m-d H:i:s` del servidor. Se compara el texto con `server_time`, no el reloj
 * del navegador. La reserva no vence sola y puede tener días, así que si no es de hoy se
 * agrega el día. Con `withPreposition`: "las 14:50" o "el 12 oct a las 00:20".
 */
export const formatReservationTime = (
  value: string,
  serverTime: string,
  withPreposition = false,
): string => {
  const date = parseServerDate(value);
  const time = format(date, 'HH:mm');
  if (value.slice(0, 10) === serverTime.slice(0, 10)) {
    return withPreposition ? `las ${time}` : time;
  }
  return withPreposition ? `el ${dayMonth(date)} a las ${time}` : `${dayMonth(date)} ${time}`;
};

/** Los `idp` de una reserva, de `group_key` ("1234,1240") */
export const reservationIdps = (reservation: Pick<MatchReservationDTO, 'group_key'>): string[] =>
  reservation.group_key.split(',').map((idp) => idp.trim());

/** La reserva cubre 2 o más pedidos (sin tarjetas, cuenta los `idp` de `group_key`) */
export const isPluralReservation = (
  reservation: Pick<MatchReservationDTO, 'group_key' | 'orders'>,
): boolean => (reservation.orders.length || reservationIdps(reservation).length) > 1;
